import './AlexaVisibility.scss';
import { useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { MdClose, MdPlayArrow } from 'react-icons/md';
import { AlexaVisibilityQuerySet } from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import { useAppSelector } from '@src/redux';
import { currentCompanySelector } from '@src/redux/company';
import { useSetError } from '@src/redux/modals';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { useTabbedNav } from '@src/utils/hooks/useNav';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { Page } from '@Components/Page/Page';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Dropdown } from '@Components/Dropdown';
import { EmptyState } from '@Components/EmptyState';
import { IconButton } from '@Components/IconButton';
import { Spinner } from '@Components/Spinner/Spinner';
import { Appearance, Size } from '@Components/designConstants';
import { equalsIgnoreCase } from '@src/utils/text-utils';
import {
  ACTIVE_RUN_STATUSES,
  ALL_RESULTS_FROM_DATE,
  AlexaVisibilityTab,
  AlexaVisibilityTabType,
  EMPTY_DATE_RANGE,
  NAVS,
  PAGE_NAME,
  QUERY_SET_OPTIONS
} from './alexa-visibility-constants';
import {
  buildScanCommand,
  filterResultsByDateRange,
  filterRunsByDateRange
} from './alexa-visibility-utils';
import { useAlexaVisibilityResults } from './hooks/useAlexaVisibilityResults';
import { useAlexaVisibilityRuns } from './hooks/useAlexaVisibilityRuns';
import { RunScanModal } from './RunScanModal';
import { QueryOverviewTab } from './Tabs/QueryOverviewTab';
import { AttributesTab } from './Tabs/AttributesTab';
import { CitationComparisonTab } from './Tabs/CitationComparisonTab';
import { CompetitiveComparisonTab } from './Tabs/CompetitiveComparisonTab';
import { TrendsTab } from './Tabs/TrendsTab';
import { AllResultsTab } from './Tabs/AllResultsTab';
import { QuestionsTab } from './Tabs/QuestionsTab';

const isTabKey = (value: string): value is AlexaVisibilityTabType =>
  (Object.values(AlexaVisibilityTab) as string[]).includes(value);

interface RunControlsProps {
  brand: string;
  onBrandChange: (brand: string) => void;
  brandOptions: string[];
  querySet: AlexaVisibilityQuerySet;
  onQuerySetChange: (querySet: AlexaVisibilityQuerySet) => void;
  onRunScrape: () => void;
  progress?: { completed: number; total: number };
  dateRange: DateRange;
  onDateRangeChange: (dateRange: DateRange) => void;
}

const RunControls = ({
  brand,
  onBrandChange,
  brandOptions,
  querySet,
  onQuerySetChange,
  onRunScrape,
  progress,
  dateRange,
  onDateRangeChange
}: RunControlsProps) => (
  <div className='aars-run-controls'>
    <div data-testid='brand-dropdown'>
      <Dropdown
        size={Size.LG}
        searchable
        creatable
        placeholder='Target brand'
        value={brand || null}
        options={brandOptions.map(option => ({ label: option, value: option }))}
        onChange={value => onBrandChange(value ?? '')}
        reactSelectProps={{ onCreateOption: onBrandChange }}
      />
    </div>
    <div data-testid='query-set-dropdown'>
      <Dropdown
        size={Size.LG}
        value={querySet}
        options={QUERY_SET_OPTIONS}
        onChange={value => onQuerySetChange(value)}
      />
    </div>
    <Button
      size={Size.LG}
      data-testid='run-scrape-button'
      leadingElement={<MdPlayArrow />}
      onClick={onRunScrape}
      disabled={!brand || Boolean(progress)}
    >
      {progress ? `Running (${progress.completed}/${progress.total})` : 'Run Scrape'}
    </Button>
    <div className='aars-date-filter' data-testid='date-filter'>
      <DatePicker
        range={dateRange.start || dateRange.end ? dateRange : undefined}
        placeholder='All dates'
        onChange={({ start, end }) => onDateRangeChange({ start, end })}
      />
      {Boolean(dateRange.start || dateRange.end) && (
        <IconButton
          appearance={Appearance.EMPTY}
          aria-label='Clear date filter'
          onClick={() => onDateRangeChange(EMPTY_DATE_RANGE)}
        >
          <MdClose />
        </IconButton>
      )}
    </div>
  </div>
);

export const AlexaVisibility = () => {
  const company = useAppSelector(currentCompanySelector);
  const { name: companyName } = useCompanyInfo();
  const setError = useSetError();

  const [brand, setBrand] = useState('');
  const [querySet, setQuerySet] = useState<AlexaVisibilityQuerySet>('all');
  // Empty means no filter: every tab aggregates across all runs. A chosen range limits every tab
  // to results scraped on those dates.
  const [dateRange, setDateRange] = useState<DateRange>(EMPTY_DATE_RANGE);

  const runsQuery = useAlexaVisibilityRuns(company);
  const runs = useMemo(() => runsQuery.data ?? [], [runsQuery.data]);

  const brandOptions = useMemo(() => {
    const brands = new Set(runs.map(run => run.brand));
    if (companyName) {
      brands.add(companyName);
    }
    return Array.from(brands);
  }, [runs, companyName]);

  // Default the target brand once run history (or the company's display name,
  // as a first-time fallback) becomes available. Must wait for the runs query to
  // settle before falling back to companyName -- companyName is available
  // synchronously from redux while runs is still loading, so without this guard
  // the fallback wins the race, sets `brand`, and the `if (brand) return` above
  // then permanently blocks the real runs[0].brand default from ever applying.
  useEffect(() => {
    if (brand) return;
    if (runsQuery.isLoading) return;
    if (runs.length > 0) {
      setBrand(runs[0].brand);
    } else if (companyName) {
      setBrand(companyName);
    }
  }, [brand, runs, companyName, runsQuery.isLoading]);

  const runsForBrand = useMemo(
    () => runs.filter(run => equalsIgnoreCase(run.brand, brand)),
    [runs, brand]
  );

  const latestRunForBrand = runsForBrand[0];
  const isRunInProgress = Boolean(
    latestRunForBrand && ACTIVE_RUN_STATUSES.has(latestRunForBrand.status)
  );

  const resultsQuery = useAlexaVisibilityResults({
    company,
    brand,
    dateFrom: ALL_RESULTS_FROM_DATE,
    latestRunId: latestRunForBrand?.id,
    latestRunStatus: latestRunForBrand?.status
  });
  const results = useMemo(() => resultsQuery.data ?? [], [resultsQuery.data]);

  // Every tab reads the same date-filtered set, so the numbers aggregate across all runs in range
  // (or all runs when no dates are chosen) instead of showing a single run.
  const filteredResults = useMemo(
    () => filterResultsByDateRange(results, dateRange.start, dateRange.end),
    [results, dateRange]
  );

  // Runs started in range, plus any run that owns a result in range (a run can start before the
  // range begins and still have results inside it). Keeps run numbers resolvable in the export.
  const filteredRuns = useMemo(() => {
    const startedInRange = new Set(
      filterRunsByDateRange(runsForBrand, dateRange.start, dateRange.end).map(run => run.id)
    );
    const hasResultInRange = new Set(filteredResults.map(result => result.runId));
    return runsForBrand.filter(run => startedInRange.has(run.id) || hasResultInRange.has(run.id));
  }, [runsForBrand, filteredResults, dateRange]);

  const noResultsInDateRange = results.length > 0 && filteredResults.length === 0;

  const [showScanModal, setShowScanModal] = useState(false);

  const { tab, goToTab } = useTabbedNav({
    baseURL: 'alexa-visibility',
    defaultKey: AlexaVisibilityTab.QUERY_OVERVIEW,
    navs: NAVS.map(nav => nav.key)
  });

  useEffect(() => {
    if (runsQuery.error) {
      setError({
        title: "Couldn't load run history",
        message: `Failed to load run history: ${runsQuery.error.message}`,
        reportError: runsQuery.error
      });
    }
  }, [runsQuery.error, setError]);

  useEffect(() => {
    if (resultsQuery.error) {
      setError({
        title: "Couldn't load results",
        message: `Failed to load results: ${resultsQuery.error.message}`,
        reportError: resultsQuery.error
      });
    }
  }, [resultsQuery.error, setError]);

  // Scans run on the user's own machine (see RunScanModal), so this only shows them how.
  const handleRunScrape = () => {
    if (!brand) return;
    setShowScanModal(true);
    Mixpanel.track(MxE.ActionTriggered, { page: PAGE_NAME, action: 'run_scrape' });
  };

  const renderTab = () => {
    // Saved questions don't depend on scraped results, so the date filter doesn't apply to them.
    if (tab === AlexaVisibilityTab.QUESTIONS) return <QuestionsTab brand={brand} />;

    if (noResultsInDateRange) {
      return (
        <EmptyState
          title='No results in this date range'
          subtitle='Choose different dates, or clear the filter to see all runs.'
        />
      );
    }

    switch (tab) {
      case AlexaVisibilityTab.ATTRIBUTES:
        return <AttributesTab results={filteredResults} targetBrand={brand} />;
      case AlexaVisibilityTab.CITATION_COMPARISON:
        return (
          <CitationComparisonTab
            results={filteredResults}
            allResults={results}
            targetBrand={brand}
          />
        );
      case AlexaVisibilityTab.COMPETITIVE_COMPARISON:
        return (
          <CompetitiveComparisonTab
            results={filteredResults}
            allResults={results}
            targetBrand={brand}
          />
        );
      case AlexaVisibilityTab.TRENDS:
        return <TrendsTab results={filteredResults} allResults={results} targetBrand={brand} />;
      case AlexaVisibilityTab.ALL_RESULTS:
        return (
          <AllResultsTab
            results={filteredResults}
            runs={filteredRuns}
            allRuns={runsForBrand}
            targetBrand={brand}
          />
        );
      case AlexaVisibilityTab.QUERY_OVERVIEW:
      default:
        return (
          <QueryOverviewTab results={filteredResults} runs={filteredRuns} targetBrand={brand} />
        );
    }
  };

  const isLoading = runsQuery.isLoading || resultsQuery.isLoading;

  return (
    <Page
      title={PAGE_NAME}
      pageType={PAGE_NAME}
      className='alexa-visibility-page'
      navs={NAVS}
      selectedNav={tab}
      onNav={nav => {
        if (isTabKey(nav)) {
          goToTab(nav);
          Mixpanel.track(MxE.TabChange, { page: PAGE_NAME, option_selected: nav });
        }
      }}
      isLoading={isLoading}
      // Run controls scope scrape runs and results only, not saved questions.
      subHeader={
        tab === AlexaVisibilityTab.QUESTIONS ? undefined : (
          <RunControls
            brand={brand}
            onBrandChange={setBrand}
            brandOptions={brandOptions}
            querySet={querySet}
            onQuerySetChange={setQuerySet}
            onRunScrape={handleRunScrape}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            progress={
              latestRunForBrand && isRunInProgress
                ? {
                    completed: latestRunForBrand.completedQueries,
                    total: latestRunForBrand.totalQueries
                  }
                : undefined
            }
          />
        )
      }
    >
      <Container fluid className='alexa-visibility-page-content'>
        {isLoading ? <Spinner /> : renderTab()}
      </Container>
      {showScanModal && (
        <RunScanModal
          command={buildScanCommand({ company, brand, querySet })}
          onHide={() => setShowScanModal(false)}
        />
      )}
    </Page>
  );
};
